import { useState } from 'react'
import { Plus, LayoutTemplate } from 'lucide-react'
import { useVisualTemplates, useVisualTemplate, useCreateTemplate, useUpdateTemplate } from '../hooks/useVisualTemplates'
import { TemplateForm } from '../components/templates/TemplateForm'
import { EmptyState } from '../components/ui/EmptyState'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'
import type { VisualTemplate } from '../lib/api'
import type { TemplateCreateRequest, TemplateUpdateRequest } from '../lib/api'

function TemplateCard({
  template,
  onEdit,
}: {
  template: VisualTemplate
  onEdit: () => void
}) {
  return (
    <div className="card flex flex-col">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="font-semibold text-slate-800">{template.name}</h3>
          <p className="text-sm text-slate-500 font-mono mt-0.5">{template.id}</p>
        </div>
        <span
          className={`shrink-0 px-2 py-0.5 rounded text-xs font-medium ${
            template.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'
          }`}
        >
          {template.is_active ? 'Activa' : 'Inactiva'}
        </span>
      </div>
      <div className="mt-2 flex flex-wrap gap-2 text-sm text-slate-600">
        {template.category && <span>Categoría: {template.category}</span>}
        {template.channel && <span>Canal: {template.channel}</span>}
      </div>
      <div className="mt-4 pt-4 border-t border-slate-100">
        <button
          type="button"
          onClick={onEdit}
          className="text-sm font-medium text-emerald-600 hover:text-emerald-700"
        >
          Editar
        </button>
      </div>
    </div>
  )
}

export function TemplatesPage() {
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)

  const { data: templates = [], isLoading } = useVisualTemplates({ solo_activos: false })
  const { data: editingTemplate } = useVisualTemplate(editingId)
  const createMutation = useCreateTemplate()
  const updateMutation = useUpdateTemplate()

  const handleCreate = async (data: TemplateCreateRequest) => {
    await createMutation.mutateAsync(data)
    setIsFormOpen(false)
  }

  const handleUpdate = async (id: string, data: TemplateUpdateRequest) => {
    await updateMutation.mutateAsync({ id, data })
    setEditingId(null)
    setIsFormOpen(false)
  }

  const handleEdit = (template: VisualTemplate) => {
    setEditingId(template.id)
    setIsFormOpen(true)
  }

  const handleCloseForm = () => {
    setIsFormOpen(false)
    setEditingId(null)
  }

  return (
    <div className="p-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Plantillas visuales</h1>
          <p className="text-slate-500 mt-1">Administra las plantillas del compositor para generación de imágenes</p>
        </div>
        <button
          onClick={() => {
            setEditingId(null)
            setIsFormOpen(true)
          }}
          className="btn btn-primary flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Nueva plantilla
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : templates.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onEdit={() => handleEdit(template)}
            />
          ))}
        </div>
      ) : (
        <div className="card">
          <EmptyState
            icon={<LayoutTemplate className="w-16 h-16" />}
            title="No hay plantillas"
            description="Crea plantillas para reutilizar en la generación visual"
            action={{
              label: 'Crear primera plantilla',
              onClick: () => setIsFormOpen(true),
            }}
          />
        </div>
      )}

      <TemplateForm
        isOpen={isFormOpen}
        onClose={handleCloseForm}
        onSubmitCreate={editingId ? undefined : handleCreate}
        onSubmitUpdate={editingId ? handleUpdate : undefined}
        initialData={editingId ? editingTemplate : null}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />
    </div>
  )
}
